import { useEffect, useState } from "react";
import { loyaltyService } from "../../services/loyaltyService";
import { customerService } from "../../services/customerService";
import Icon from "../../components/Common/Icon";
import AsyncCustomerSelect from "../../components/Common/AsyncCustomerSelect";
import { useToast } from "../../context/ToastContext";

const fmtDateTime = (d) =>
  d ? new Date(d).toLocaleString("tr-TR", { dateStyle: "short", timeStyle: "short" }) : "—";

const TX_LABEL = { EARN: "Kazanım", REDEEM: "Harcama", ADJUST: "Ayar", EXPIRE: "İptal" };
const TX_STYLE = {
  EARN: "bg-success/10 text-success",
  REDEEM: "bg-primary/10 text-primary",
  ADJUST: "bg-tier-gold/10 text-tier-gold",
  EXPIRE: "bg-error/10 text-error",
};

export default function LoyaltyPage() {
  const { showToast } = useToast();

  // Ayarlar
  const [settings, setSettings] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);

  // Seçili müşteri + geçmiş
  const [customerId, setCustomerId] = useState("");
  const [customer, setCustomer] = useState(null);
  const [history, setHistory] = useState([]);
  const [loadingHist, setLoadingHist] = useState(false);

  // Puan işlemi
  const [opPoints, setOpPoints] = useState("");
  const [opReason, setOpReason] = useState("");
  const [opBusy, setOpBusy] = useState(false);

  useEffect(() => {
    loyaltyService
      .getSettings()
      .then((res) => setSettings(res.data?.data ?? null))
      .catch(() => showToast("Sadakat ayarları yüklenemedi.", "error"));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadCustomer = async (id) => {
    if (!id) {
      setCustomer(null);
      setHistory([]);
      return;
    }
    setLoadingHist(true);
    try {
      const [cRes, hRes] = await Promise.all([
        customerService.get(id),
        loyaltyService.customerHistory(id, { size: 50 }),
      ]);
      setCustomer(cRes.data?.data ?? null);
      setHistory(hRes.data?.data?.content ?? []);
    } catch {
      showToast("Müşteri geçmişi yüklenemedi.", "error");
    } finally {
      setLoadingHist(false);
    }
  };

  const onPickCustomer = (id) => {
    setCustomerId(id);
    setOpPoints("");
    setOpReason("");
    loadCustomer(id);
  };

  const saveSettings = async (e) => {
    e.preventDefault();
    setSavingSettings(true);
    try {
      const payload = {
        earnPointsPerCurrency: Number(settings.earnPointsPerCurrency),
        redeemValuePerPoint: Number(settings.redeemValuePerPoint),
        minRedeemPoints: Number(settings.minRedeemPoints) || 0,
        active: settings.active,
      };
      const res = await loyaltyService.updateSettings(payload);
      setSettings(res.data?.data ?? settings);
      showToast("Sadakat kuralı kaydedildi.", "success");
    } catch (err) {
      showToast(err?.response?.data?.message || "Kaydedilemedi.", "error");
    } finally {
      setSavingSettings(false);
    }
  };

  const runOp = async (kind) => {
    const pts = Number(opPoints);
    if (!customerId) return showToast("Önce müşteri seçin.", "error");
    if (!pts || pts <= 0) return showToast("Geçerli bir puan girin.", "error");
    setOpBusy(true);
    try {
      if (kind === "earn") {
        await loyaltyService.adjust(customerId, { points: pts, reason: opReason || null });
        showToast(`${pts} puan eklendi.`, "success");
      } else if (kind === "deduct") {
        await loyaltyService.adjust(customerId, { points: -pts, reason: opReason || null });
        showToast(`${pts} puan düşüldü.`, "success");
      } else {
        const res = await loyaltyService.redeem(customerId, { points: pts, reason: opReason || null });
        const val = res.data?.data?.redeemedValue;
        showToast(`${pts} puan harcandı${val ? ` (${Number(val).toLocaleString("tr-TR")} ₺)` : ""}.`, "success");
      }
      setOpPoints("");
      setOpReason("");
      loadCustomer(customerId);
    } catch (err) {
      showToast(err?.response?.data?.message || "İşlem başarısız.", "error");
    } finally {
      setOpBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Başlık */}
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
          <Icon name="loyalty" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-on-surface">Sadakat Puanı</h1>
          <p className="text-on-surface-variant text-sm">Puan kazanım kuralı ve müşteri puan işlemleri.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Ayarlar */}
        <form onSubmit={saveSettings} className="bg-surface-card rounded-2xl border border-outline-variant p-5 space-y-4">
          <h3 className="font-bold text-on-surface flex items-center gap-2">
            <Icon name="tune" className="!text-[20px] text-primary" /> Kazanım Kuralı
          </h3>
          {!settings ? (
            <p className="text-sm text-on-surface-variant">Yükleniyor…</p>
          ) : (
            <>
              <div>
                <label className="block text-xs font-semibold mb-1 text-on-surface-variant">
                  1 TL harcamada kazanılan puan
                </label>
                <input type="number" step="any" min="0" value={settings.earnPointsPerCurrency}
                  onChange={(e) => setSettings({ ...settings, earnPointsPerCurrency: e.target.value })}
                  className="w-full bg-white border border-outline-variant rounded-xl px-3 py-2 text-sm outline-none focus:border-primary" />
                <p className="text-[11px] text-on-surface-variant mt-1">
                  Örn. 0.1 → her 10 TL harcamada 1 puan.
                </p>
              </div>
              <div>
                <label className="block text-xs font-semibold mb-1 text-on-surface-variant">
                  1 puanın TL değeri (harcarken)
                </label>
                <input type="number" step="any" min="0" value={settings.redeemValuePerPoint}
                  onChange={(e) => setSettings({ ...settings, redeemValuePerPoint: e.target.value })}
                  className="w-full bg-white border border-outline-variant rounded-xl px-3 py-2 text-sm outline-none focus:border-primary" />
              </div>
              <div>
                <label className="block text-xs font-semibold mb-1 text-on-surface-variant">
                  Harcama için en az puan
                </label>
                <input type="number" min="0" value={settings.minRedeemPoints}
                  onChange={(e) => setSettings({ ...settings, minRedeemPoints: e.target.value })}
                  className="w-full bg-white border border-outline-variant rounded-xl px-3 py-2 text-sm outline-none focus:border-primary" />
              </div>
              <label className="flex items-center gap-2 text-sm text-on-surface">
                <input type="checkbox" checked={settings.active}
                  onChange={(e) => setSettings({ ...settings, active: e.target.checked })} />
                Tahsilatta otomatik puan kazanımı açık
              </label>
              <button type="submit" disabled={savingSettings}
                className="w-full bg-primary text-white py-2.5 rounded-xl text-sm font-semibold hover:opacity-90 transition disabled:opacity-50">
                {savingSettings ? "Kaydediliyor…" : "Kuralı Kaydet"}
              </button>
            </>
          )}
        </form>

        {/* Müşteri puan işlemi */}
        <div className="bg-surface-card rounded-2xl border border-outline-variant p-5 space-y-4">
          <h3 className="font-bold text-on-surface flex items-center gap-2">
            <Icon name="person" className="!text-[20px] text-primary" /> Müşteri Puan İşlemi
          </h3>
          <AsyncCustomerSelect value={customerId} onChange={onPickCustomer} />

          {customer && (
            <div className="bg-surface-container-low rounded-xl p-4 flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-on-surface">{customer.fullName}</p>
                <p className="text-xs text-on-surface-variant">Güncel bakiye</p>
              </div>
              <div className="text-right">
                <span className="text-2xl font-bold text-primary">{customer.loyaltyPoints ?? 0}</span>
                <span className="text-xs text-on-surface-variant ml-1">puan</span>
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold mb-1 text-on-surface-variant">Puan</label>
              <input type="number" min="1" value={opPoints} onChange={(e) => setOpPoints(e.target.value)}
                disabled={!customerId}
                className="w-full bg-white border border-outline-variant rounded-xl px-3 py-2 text-sm outline-none focus:border-primary disabled:bg-surface-container-high" />
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1 text-on-surface-variant">Açıklama</label>
              <input value={opReason} onChange={(e) => setOpReason(e.target.value)} disabled={!customerId}
                className="w-full bg-white border border-outline-variant rounded-xl px-3 py-2 text-sm outline-none focus:border-primary disabled:bg-surface-container-high" />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-2">
            <button onClick={() => runOp("earn")} disabled={!customerId || opBusy}
              className="flex items-center justify-center gap-1 bg-success/10 text-success py-2 rounded-xl text-xs font-semibold hover:bg-success/20 transition disabled:opacity-50">
              <Icon name="add" className="!text-[18px]" /> Ekle
            </button>
            <button onClick={() => runOp("deduct")} disabled={!customerId || opBusy}
              className="flex items-center justify-center gap-1 bg-tier-gold/10 text-tier-gold py-2 rounded-xl text-xs font-semibold hover:bg-tier-gold/20 transition disabled:opacity-50">
              <Icon name="remove" className="!text-[18px]" /> Düş
            </button>
            <button onClick={() => runOp("redeem")} disabled={!customerId || opBusy}
              className="flex items-center justify-center gap-1 bg-primary/10 text-primary py-2 rounded-xl text-xs font-semibold hover:bg-primary/20 transition disabled:opacity-50">
              <Icon name="redeem" className="!text-[18px]" /> Harca
            </button>
          </div>
        </div>
      </div>

      {/* Geçmiş */}
      {customerId && (
        <div className="bg-surface-card rounded-2xl border border-outline-variant p-5">
          <h3 className="font-bold text-on-surface mb-3 flex items-center gap-2">
            <Icon name="history" className="!text-[20px] text-primary" /> Puan Geçmişi
          </h3>
          {loadingHist ? (
            <p className="text-sm text-on-surface-variant py-4 text-center">Yükleniyor…</p>
          ) : history.length === 0 ? (
            <p className="text-sm text-on-surface-variant py-4 text-center">Bu müşteride puan hareketi yok.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-on-surface-variant border-b border-outline-variant">
                    <th className="py-2 pr-3">Tarih</th>
                    <th className="py-2 pr-3">Tür</th>
                    <th className="py-2 pr-3 text-right">Puan</th>
                    <th className="py-2 pr-3 text-right">Bakiye</th>
                    <th className="py-2">Açıklama</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((t) => (
                    <tr key={t.id} className="border-b border-outline-variant/50">
                      <td className="py-2 pr-3 text-on-surface-variant whitespace-nowrap">{fmtDateTime(t.createdAt)}</td>
                      <td className="py-2 pr-3">
                        <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${TX_STYLE[t.type]}`}>
                          {TX_LABEL[t.type] ?? t.type}
                        </span>
                      </td>
                      <td className={`py-2 pr-3 text-right font-semibold ${t.points >= 0 ? "text-success" : "text-error"}`}>
                        {t.points >= 0 ? `+${t.points}` : t.points}
                      </td>
                      <td className="py-2 pr-3 text-right font-medium text-on-surface">{t.balanceAfter}</td>
                      <td className="py-2 text-on-surface-variant">{t.reason || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
